import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError, mediaUrl, REACTIONS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRealtime } from "../context/RealtimeContext";
import { Avatar } from "../components/Avatar";
import { LinkPreview } from "../components/LinkPreview";
import { GifPicker } from "../components/GifPicker";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Send, Flag, ImagePlus, X, Loader2, Users, Settings, LogOut, UserPlus, Check, Search, Reply, SmilePlus, PawPrint, Flame, Heart, Star, Frown, ThumbsDown, MessageCircle } from "lucide-react";
import { ReportDialog } from "../components/ReportDialog";
import { toast } from "sonner";

const RICONS = { PawPrint, Flame, Heart, Star, Frown, ThumbsDown };
const RByType = Object.fromEntries(REACTIONS.map((r) => [r.type, r]));
function ReactionIcon({ type, className }) {
  const r = RByType[type]; if (!r) return null;
  const Icon = RICONS[r.icon] || PawPrint;
  return <Icon className={className} style={{ color: r.color }} />;
}

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const t = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    return d.toDateString() === now.toDateString() ? t : `${d.toLocaleDateString([], { month: "short", day: "numeric" })} · ${t}`;
  } catch (e) { return ""; }
};

function MemberPicker({ exclude = [], selected, onToggle }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/users/search?q=${encodeURIComponent(q)}`); setResults(data.filter((u) => !exclude.includes(u.id))); } catch (e) {}
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-spotter-maroon/60" />
        <Input data-testid="group-member-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members…" className="pl-9 border-2 border-black" />
      </div>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="group-selected-members">
          {selected.map((u) => (
            <span key={u.id} className="inline-flex items-center gap-1 bg-spotter-gold/30 border-2 border-black rounded-full px-2 py-0.5 text-xs font-bold">
              {u.name}<button onClick={() => onToggle(u)}><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
      {results.length > 0 && (
        <div className="mt-2 border-2 border-black rounded-lg max-h-52 overflow-y-auto bg-white">
          {results.slice(0, 8).map((u) => {
            const on = selected.find((s) => s.id === u.id);
            return (
              <button key={u.id} data-testid={`group-member-result-${u.id}`} onClick={() => onToggle(u)}
                className={`w-full flex items-center gap-2 p-2 text-left hover:bg-spotter-cream ${on ? "bg-spotter-gold/20" : ""}`}>
                <Avatar user={u} size="sm" /><span className="text-sm font-semibold flex-1">{u.name} <span className="text-spotter-maroon font-normal">@{u.username}</span></span>
                {on && <Check className="w-4 h-4 text-green-700" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Messages() {
  const { id, gid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const rt = useRealtime();
  const [convos, setConvos] = useState([]);
  const [groups, setGroups] = useState([]);
  const [active, setActive] = useState(null);
  const [activeGroup, setActiveGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [otherTyping, setOtherTyping] = useState(false);
  const [groupTypers, setGroupTypers] = useState({});
  const [dmRead, setDmRead] = useState(false);
  const groupTyperTimers = useRef({});
  const endRef = useRef();
  const typingTimer = useRef();
  const imgRef = useRef();
  const [reportMsgId, setReportMsgId] = useState(null);
  const [pendingImage, setPendingImage] = useState("");
  const [imgUploading, setImgUploading] = useState(false);
  const [newGroupOpen, setNewGroupOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groupPick, setGroupPick] = useState([]);
  const [manageOpen, setManageOpen] = useState(false);
  const [addPick, setAddPick] = useState([]);
  const [replyTo, setReplyTo] = useState(null);

  const loadConvos = async () => {
    try { const [d, g] = await Promise.all([api.get("/conversations"), api.get("/group-conversations")]); setConvos(d.data); setGroups(g.data); } catch (e) {}
  };

  useEffect(() => { loadConvos(); }, []);
  useEffect(() => { if (rt) return rt.registerConvHandler(loadConvos); }, [rt]);

  // open DM from URL
  useEffect(() => {
    if (gid || !id) { if (!gid) { setActive(null); setMessages([]); } return; }
    setActiveGroup(null);
    (async () => {
      let u = convos.find((c) => c.user.id === id)?.user;
      if (!u) { const { data } = await api.get(`/users/${id}`); u = data; }
      setActive(u);
      const { data } = await api.get(`/messages/${id}`);
      setMessages(data);
      const lastMine = [...data].reverse().find((m) => m.from_user === user.id);
      setDmRead(!!(lastMine && lastMine.read));
      loadConvos();
      rt?.refreshNotifs();
    })();
  }, [id, convos.length]);

  // open group from URL
  useEffect(() => {
    if (!gid) { setActiveGroup(null); return; }
    setActive(null);
    (async () => {
      try {
        const { data: g } = await api.get(`/group-conversations/${gid}`);
        setActiveGroup(g);
        const { data } = await api.get(`/group-conversations/${gid}/messages`);
        setMessages(data);
        loadConvos();
        rt?.refreshNotifs();
      } catch (e) { toast.error("Group not found"); navigate("/messages"); }
    })();
  }, [gid]);

  useEffect(() => { setReplyTo(null); }, [id, gid]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, otherTyping]);

  // realtime incoming DMs
  useEffect(() => {
    if (!rt) return;
    return rt.registerMessageHandler((m) => {
      if (active && m.from_user === active.id) { setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m])); api.post(`/messages/${active.id}/read`); }
      loadConvos();
    });
  }, [rt, active]);

  // realtime incoming group messages
  useEffect(() => {
    if (!rt) return;
    return rt.registerGroupMsgHandler((m) => {
      if (activeGroup && m.conversation_id === activeGroup.id) {
        setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
        api.get(`/group-conversations/${activeGroup.id}/messages`).then(({ data }) => setMessages(data)).catch(() => {});
      }
      loadConvos();
    });
  }, [rt, activeGroup]);

  // typing indicator (DM + group)
  useEffect(() => {
    if (!rt) return;
    return rt.registerTypingHandler((t) => {
      if (t.channel === "group") {
        if (!activeGroup || t.channel_id !== activeGroup.id) return;
        setGroupTypers((prev) => {
          const next = { ...prev };
          if (t.typing) next[t.from] = t.from_name || "Someone"; else delete next[t.from];
          return next;
        });
        clearTimeout(groupTyperTimers.current[t.from]);
        if (t.typing) groupTyperTimers.current[t.from] = setTimeout(() => setGroupTypers((prev) => { const n = { ...prev }; delete n[t.from]; return n; }), 3000);
      } else {
        if (active && t.from === active.id) {
          setOtherTyping(t.typing);
          if (t.typing) { clearTimeout(typingTimer.current); typingTimer.current = setTimeout(() => setOtherTyping(false), 3000); }
        }
      }
    });
  }, [rt, active, activeGroup]);

  // read receipts (DM + group)
  useEffect(() => {
    if (!rt) return;
    return rt.registerReadHandler((r) => {
      if (r.channel === "dm" && active && r.by === active.id) setDmRead(true);
      else if (r.channel === "group" && activeGroup && r.channel_id === activeGroup.id) {
        api.get(`/group-conversations/${activeGroup.id}/messages`).then(({ data }) => setMessages(data)).catch(() => {});
      }
    });
  }, [rt, active, activeGroup]);

  // realtime reaction updates (DM + group)
  useEffect(() => {
    if (!rt?.registerReactionHandler) return;
    return rt.registerReactionHandler((r) => {
      if ((r.channel === "dm" && active) || (r.channel === "group" && activeGroup && r.channel_id === activeGroup.id)) {
        setMessages((prev) => prev.map((x) => (x.id === r.message_id ? { ...x, reactions: r.reactions } : x)));
      }
    });
  }, [rt, active, activeGroup]);

  const reactMessage = async (m, type) => {
    const current = (m.reactions || []).find((r) => r.user_id === user.id);
    const newType = current && current.type === type ? null : type;
    const optimistic = (m.reactions || []).filter((r) => r.user_id !== user.id);
    if (newType) optimistic.push({ user_id: user.id, user_name: user.name, type: newType });
    setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, reactions: optimistic } : x)));
    try {
      const url = activeGroup ? `/group-conversations/${activeGroup.id}/messages/${m.id}/react` : `/messages/${m.id}/react`;
      const { data } = await api.post(url, { type: newType });
      setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, reactions: data.reactions } : x)));
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const onType = (e) => {
    setText(e.target.value);
    if (active) { rt.sendTyping(active.id, true, "dm"); typingTimer.current = setTimeout(() => rt.sendTyping(active.id, false, "dm"), 1500); }
    else if (activeGroup) { rt.sendTyping(null, true, "group", activeGroup.id); typingTimer.current = setTimeout(() => rt.sendTyping(null, false, "group", activeGroup.id), 1500); }
  };

  const pickImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return; e.target.value = "";
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image."); return; }
    setImgUploading(true);
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setPendingImage(data.url); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setImgUploading(false); }
  };

  const send = async () => {
    if (!text.trim() && !pendingImage) return;
    try {
      if (activeGroup) {
        const { data } = await api.post(`/group-conversations/${activeGroup.id}/messages`, { text, image_url: pendingImage || null, reply_to_id: replyTo?.id || null });
        setMessages((prev) => [...prev, data]);
        rt.sendTyping(null, false, "group", activeGroup.id);
      } else if (active) {
        const { data } = await api.post("/messages", { to_user_id: active.id, text, image_url: pendingImage || null, reply_to_id: replyTo?.id || null });
        setMessages((prev) => [...prev, data]);
        setDmRead(false);
        rt.sendTyping(active.id, false, "dm");
      } else return;
      setText(""); setPendingImage(""); setReplyTo(null); loadConvos();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const createGroup = async () => {
    if (groupPick.length < 2) { toast.error("Pick at least 2 members."); return; }
    try {
      const { data } = await api.post("/group-conversations", { name: groupName.trim(), member_ids: groupPick.map((u) => u.id) });
      setNewGroupOpen(false); setGroupName(""); setGroupPick([]); loadConvos();
      navigate(`/messages/group/${data.id}`);
      toast.success("Group created!");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const addMembers = async () => {
    if (!addPick.length) return;
    try { const { data } = await api.post(`/group-conversations/${activeGroup.id}/members`, { add: addPick.map((u) => u.id) }); setActiveGroup(data); setAddPick([]); toast.success("Members added"); loadConvos(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const removeMember = async (uid) => {
    try { const { data } = await api.post(`/group-conversations/${activeGroup.id}/members`, { remove: [uid] }); setActiveGroup(data); loadConvos(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const renameGroup = async (name) => {
    try { await api.put(`/group-conversations/${activeGroup.id}`, { name }); setActiveGroup((g) => ({ ...g, name: name.trim() || g.name, custom_name: name.trim() })); loadConvos(); toast.success("Group renamed"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const leaveGroup = async () => {
    try { await api.post(`/group-conversations/${activeGroup.id}/leave`); setManageOpen(false); loadConvos(); navigate("/messages"); toast.success("You left the group"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const showThread = active || activeGroup;

  return (
    <div className="flex gap-0 border-2 border-black rounded-xl overflow-hidden h-[70vh] bg-white">
      {/* Sidebar */}
      <div className={`w-full sm:w-72 border-r-2 border-black overflow-y-auto bg-spotter-cream/30 ${showThread ? "hidden sm:block" : "block"}`}>
        {/* GROUP CHATS CARD */}
        <div className="m-2 border-2 border-black rounded-xl bg-white overflow-hidden brand-shadow-sm" data-testid="group-chats-card">
          <div className="p-2.5 font-display text-lg font-bold text-spotter-maroon border-b-2 border-black bg-spotter-cream flex items-center justify-between">
            <span className="flex items-center gap-1.5"><Users className="w-4 h-4" />Group Chats</span>
            <Button data-testid="new-group-btn" size="sm" onClick={() => setNewGroupOpen(true)} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-1 text-xs h-8"><UserPlus className="w-3.5 h-3.5" />New</Button>
          </div>
          {groups.length === 0
            ? <div className="p-3 text-xs text-spotter-maroon">No group chats yet — tap “New” to start one.</div>
            : groups.map((g) => (
              <button key={g.id} data-testid={`group-convo-${g.id}`} onClick={() => navigate(`/messages/group/${g.id}`)}
                className={`w-full flex items-center gap-3 p-3 border-b border-black/10 text-left hover:bg-spotter-cream ${activeGroup?.id === g.id ? "bg-spotter-gold/20" : ""}`}>
                <div className="w-10 h-10 rounded-full border-2 border-black bg-spotter-gold/40 flex items-center justify-center shrink-0"><Users className="w-5 h-5 text-spotter-maroon" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm flex items-center justify-between truncate">{g.name}
                    {g.unread > 0 && <span data-testid={`group-unread-${g.id}`} className="ml-2 min-w-[20px] h-5 px-1.5 text-xs font-bold rounded-full bg-spotter-maroon text-spotter-cream flex items-center justify-center">{g.unread}</span>}
                  </div>
                  <div className="text-xs text-spotter-maroon truncate">{g.members.length} members · {g.last_message}</div>
                </div>
              </button>
            ))}
        </div>
        {/* PRIVATE MESSAGES CARD */}
        <div className="m-2 border-2 border-black rounded-xl bg-white overflow-hidden brand-shadow-sm" data-testid="dms-card">
          <div className="p-2.5 font-display text-lg font-bold text-spotter-maroon border-b-2 border-black bg-spotter-cream flex items-center gap-1.5"><MessageCircle className="w-4 h-4" />Private Messages</div>
          {convos.length === 0
            ? <div className="p-3 text-xs text-spotter-maroon">No private messages yet. Message someone in your Pride.</div>
            : convos.map((c) => (
              <button key={c.user.id} data-testid={`convo-${c.user.id}`} onClick={() => navigate(`/messages/${c.user.id}`)}
                className={`w-full flex items-center gap-3 p-3 border-b border-black/10 text-left hover:bg-spotter-cream ${active?.id === c.user.id ? "bg-spotter-gold/20" : ""}`}>
                <Avatar user={c.user} size="md" showPresence />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm flex items-center justify-between">{c.user.name}
                    {c.unread > 0 && <span data-testid={`convo-unread-${c.user.id}`} className="ml-2 min-w-[20px] h-5 px-1.5 text-xs font-bold rounded-full bg-spotter-maroon text-spotter-cream flex items-center justify-center">{c.unread}</span>}
                  </div>
                  <div className="text-xs text-spotter-maroon truncate">{c.last_message}</div>
                </div>
              </button>
            ))}
        </div>
      </div>

      {/* Thread */}
      <div className={`flex-1 flex flex-col ${showThread ? "flex" : "hidden sm:flex"}`}>
        {!showThread ? (
          <div className="flex-1 flex items-center justify-center text-spotter-maroon">Select a conversation</div>
        ) : activeGroup ? (
          <>
            <div className="p-3 border-b-2 border-black bg-spotter-cream flex items-center gap-3">
              <div className="w-10 h-10 rounded-full border-2 border-black bg-spotter-gold/40 flex items-center justify-center shrink-0"><Users className="w-5 h-5 text-spotter-maroon" /></div>
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate" data-testid="group-header-name">{activeGroup.name}</div>
                <div className="text-xs text-spotter-maroon truncate">{activeGroup.members.map((m) => m.name).join(", ")}</div>
              </div>
              <Button data-testid="group-manage-btn" size="sm" variant="outline" onClick={() => setManageOpen(true)} className="border-2 border-black px-2"><Settings className="w-4 h-4" /></Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-white" data-testid="message-thread">
              {messages.map((m) => {
                const mine = m.from_user === user.id;
                return (
                  <div key={m.id} className={`group flex items-end gap-2 ${mine ? "justify-end" : "justify-start"}`}>
                    {!mine && <Avatar user={{ id: m.from_user, name: m.from_name, avatar_url: m.from_avatar }} size="sm" />}
                    <div className={`max-w-[75%] px-3 py-2 rounded-2xl border-2 border-black ${mine ? "bg-spotter-maroon text-spotter-cream" : "bg-spotter-cream"}`}>
                      {!mine && <div className="text-[11px] font-bold text-spotter-maroon mb-0.5">{m.from_name}</div>}
                      {m.reply_to && <div data-testid={`reply-quote-${m.id}`} className={`text-[11px] mb-1 pl-2 border-l-2 ${mine ? "border-spotter-cream/50 text-spotter-cream/80" : "border-spotter-maroon/40 text-spotter-maroon/80"}`}><span className="font-bold">{m.reply_to.from_name}</span><div className="truncate max-w-[220px]">{m.reply_to.text}</div></div>}
                      {m.image_url && <img src={mediaUrl(m.image_url)} alt="" onClick={() => window.open(mediaUrl(m.image_url), "_blank")} className="rounded-lg mb-1 max-h-64 w-auto cursor-pointer border border-black/20" />}
                      {m.text && <div className="text-sm whitespace-pre-wrap">{m.text}</div>}
                      {m.text && <LinkPreview text={m.text} className="max-w-[260px]" />}
                      <div className={`text-[10px] mt-0.5 ${mine ? "text-spotter-cream/60" : "text-spotter-maroon/50"}`}>{fmtTime(m.created_at)}</div>
                      {reactChips(m, mine)}
                    </div>
                    <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                      {reactTrigger(m)}
                      <button data-testid={`reply-message-${m.id}`} onClick={() => setReplyTo({ id: m.id, from_name: mine ? "You" : m.from_name, text: m.text || (m.image_url ? "📷 Photo" : "") })} title="Reply" className="text-spotter-maroon hover:text-spotter-gold p-1"><Reply className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                );
              })}
              {activeGroup && (() => {
                const lastMine = [...messages].reverse().find((m) => m.from_user === user.id);
                if (!lastMine) return null;
                const seen = (lastMine.read_by || []).filter((uid) => uid !== user.id).length;
                return seen > 0 ? <div data-testid="group-seen" className="text-[11px] text-spotter-maroon/60 text-right pr-1">Seen by {seen}</div> : null;
              })()}
              <div ref={endRef} />
            </div>
            {activeGroup && Object.keys(groupTypers).length > 0 && (
              <div data-testid="group-typing-indicator" className="px-4 pb-1 text-xs text-spotter-maroon italic">{Object.values(groupTypers).join(", ")} {Object.keys(groupTypers).length > 1 ? "are" : "is"} typing…</div>
            )}
            {renderComposer()}
          </>
        ) : (
          <>
            <div className="p-3 border-b-2 border-black bg-spotter-cream flex items-center gap-3">
              <Avatar user={active} size="md" showPresence />
              <div>
                <button onClick={() => navigate(`/profile/${active.id}`)} className="font-bold hover:underline">{active.name}</button>
                <div className="text-xs text-spotter-maroon">{rt.isOnline(active.id) ? "Online" : "Offline"}</div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-white" data-testid="message-thread">
              {messages.map((m) => {
                const mine = m.from_user === user.id;
                return (
                  <div key={m.id} className={`group flex items-center gap-1 ${mine ? "justify-end" : "justify-start"}`}>
                    {mine && (
                      <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                        {reactTrigger(m)}
                        <button data-testid={`reply-message-${m.id}`} onClick={() => setReplyTo({ id: m.id, from_name: "You", text: m.text || (m.image_url ? "📷 Photo" : "") })} title="Reply"
                          className="text-spotter-maroon hover:text-spotter-gold p-1"><Reply className="w-3.5 h-3.5" /></button>
                      </div>
                    )}
                    <div className={`max-w-[75%] px-3 py-2 rounded-2xl border-2 border-black ${mine ? "bg-spotter-maroon text-spotter-cream" : "bg-spotter-cream"}`}>
                      {m.reply_to && <div data-testid={`reply-quote-${m.id}`} className={`text-[11px] mb-1 pl-2 border-l-2 ${mine ? "border-spotter-cream/50 text-spotter-cream/80" : "border-spotter-maroon/40 text-spotter-maroon/80"}`}><span className="font-bold">{m.reply_to.from_name}</span><div className="truncate max-w-[220px]">{m.reply_to.text}</div></div>}
                      {m.image_url && <img src={mediaUrl(m.image_url)} alt="" data-testid={`message-image-${m.id}`} onClick={() => window.open(mediaUrl(m.image_url), "_blank")} className="rounded-lg mb-1 max-h-64 w-auto cursor-pointer border border-black/20" />}
                      {m.text && <div className="text-sm whitespace-pre-wrap">{m.text}</div>}
                      {m.text && <LinkPreview text={m.text} className="max-w-[260px]" />}
                      <div className={`text-[10px] mt-0.5 ${mine ? "text-spotter-cream/60" : "text-spotter-maroon/50"}`}>{fmtTime(m.created_at)}</div>
                      {reactChips(m, mine)}
                    </div>
                    {!mine && (
                      <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                        {reactTrigger(m)}
                        <button data-testid={`reply-message-${m.id}`} onClick={() => setReplyTo({ id: m.id, from_name: active.name, text: m.text || (m.image_url ? "📷 Photo" : "") })} title="Reply"
                          className="text-spotter-maroon hover:text-spotter-gold p-1"><Reply className="w-3.5 h-3.5" /></button>
                        <button data-testid={`report-message-${m.id}`} onClick={() => setReportMsgId(m.id)} title="Report message"
                          className="text-spotter-maroon hover:text-red-700 p-1"><Flag className="w-3.5 h-3.5" /></button>
                      </div>
                    )}
                  </div>
                );
              })}
              {otherTyping && <div data-testid="typing-indicator" className="text-xs text-spotter-maroon italic">{active.name} is typing…</div>}
              {!otherTyping && dmRead && messages.length > 0 && messages[messages.length - 1].from_user === user.id && (
                <div data-testid="dm-seen" className="text-[11px] text-spotter-maroon/60 text-right pr-1">Seen</div>
              )}
              <div ref={endRef} />
            </div>
            {renderComposer()}
          </>
        )}
      </div>

      {/* New group dialog */}
      <Dialog open={newGroupOpen} onOpenChange={setNewGroupOpen}>
        <DialogContent className="max-w-md border-2 border-black bg-spotter-cream" data-testid="new-group-dialog">
          <DialogTitle className="font-display text-2xl font-bold text-spotter-maroon flex items-center gap-2"><Users className="w-5 h-5" />New Group Chat</DialogTitle>
          <DialogDescription className="sr-only">Create a group chat</DialogDescription>
          <Input data-testid="new-group-name" value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Group name (optional)" className="border-2 border-black bg-white" />
          <MemberPicker exclude={[user.id]} selected={groupPick} onToggle={(u) => setGroupPick((p) => p.find((x) => x.id === u.id) ? p.filter((x) => x.id !== u.id) : [...p, u])} />
          <Button data-testid="create-group-btn" onClick={createGroup} disabled={groupPick.length < 2} className="bg-spotter-maroon text-spotter-cream border-2 border-black">Create group ({groupPick.length} added)</Button>
        </DialogContent>
      </Dialog>

      {/* Manage group dialog */}
      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="max-w-md border-2 border-black bg-spotter-cream" data-testid="manage-group-dialog">
          <DialogTitle className="font-display text-2xl font-bold text-spotter-maroon">Group settings</DialogTitle>
          <DialogDescription className="sr-only">Manage group members and name</DialogDescription>
          {activeGroup && (
            <>
              <div className="flex gap-2">
                <Input data-testid="rename-group-input" defaultValue={activeGroup.custom_name || ""} placeholder="Group name" id="rename-group-field" className="border-2 border-black bg-white" />
                <Button data-testid="rename-group-btn" onClick={() => renameGroup(document.getElementById("rename-group-field").value)} className="bg-spotter-maroon text-spotter-cream border-2 border-black">Save</Button>
              </div>
              <div>
                <div className="text-sm font-bold text-spotter-maroon mb-1">Members ({activeGroup.members.length})</div>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {activeGroup.members.map((m) => (
                    <div key={m.id} className="flex items-center gap-2 bg-white border-2 border-black rounded-lg p-1.5" data-testid={`group-member-${m.id}`}>
                      <Avatar user={m} size="sm" /><span className="text-sm flex-1">{m.name}{m.id === activeGroup.created_by && <span className="text-xs text-spotter-maroon"> · creator</span>}</span>
                      {m.id !== activeGroup.created_by && <button data-testid={`group-remove-${m.id}`} onClick={() => removeMember(m.id)} className="text-spotter-maroon hover:text-red-700"><X className="w-4 h-4" /></button>}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-sm font-bold text-spotter-maroon mb-1 flex items-center gap-1"><UserPlus className="w-4 h-4" />Add members</div>
                <MemberPicker exclude={activeGroup.members.map((m) => m.id)} selected={addPick} onToggle={(u) => setAddPick((p) => p.find((x) => x.id === u.id) ? p.filter((x) => x.id !== u.id) : [...p, u])} />
                {addPick.length > 0 && <Button data-testid="add-members-btn" onClick={addMembers} className="mt-2 bg-spotter-maroon text-spotter-cream border-2 border-black">Add {addPick.length}</Button>}
              </div>
              <Button data-testid="leave-group-btn" variant="outline" onClick={leaveGroup} className="border-2 border-black text-red-700 gap-1"><LogOut className="w-4 h-4" />Leave group</Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ReportDialog open={!!reportMsgId} onOpenChange={(o) => !o && setReportMsgId(null)} targetType="message" targetId={reportMsgId} targetName={active?.name} />
    </div>
  );

  function reactChips(m, mine) {
    const counts = {};
    (m.reactions || []).forEach((r) => { counts[r.type] = (counts[r.type] || 0) + 1; });
    const types = REACTIONS.map((r) => r.type).filter((t) => counts[t]);
    if (!types.length) return null;
    return (
      <div className="flex flex-wrap gap-1 mt-1" data-testid={`msg-reactions-${m.id}`}>
        {types.map((t) => (
          <span key={t} className="inline-flex items-center gap-0.5 bg-white border-2 border-black rounded-full px-1.5 py-0.5 text-[11px] font-bold text-spotter-maroon">
            <ReactionIcon type={t} className="w-3 h-3" />{counts[t]}
          </span>
        ))}
      </div>
    );
  }

  function reactTrigger(m) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button data-testid={`msg-react-trigger-${m.id}`} title="React" className="text-spotter-maroon hover:text-spotter-gold p-1"><SmilePlus className="w-3.5 h-3.5" /></button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-1.5 border-2 border-black bg-white flex gap-1" side="top">
          {REACTIONS.map((r) => (
            <button key={r.type} data-testid={`msg-react-${r.type}-${m.id}`} title={r.label} onClick={() => reactMessage(m, r.type)} className="p-1 hover:scale-125 transition-transform">
              <ReactionIcon type={r.type} className="w-5 h-5" />
            </button>
          ))}
        </PopoverContent>
      </Popover>
    );
  }

  function renderComposer() {
    return (
      <div className="border-t-2 border-black bg-spotter-cream">
        {replyTo && (
          <div className="px-3 pt-2 flex items-center gap-2" data-testid="reply-preview">
            <div className="flex-1 min-w-0 pl-2 border-l-2 border-spotter-gold">
              <div className="text-[11px] font-bold text-spotter-maroon">Replying to {replyTo.from_name}</div>
              <div className="text-xs text-spotter-maroon/70 truncate">{replyTo.text}</div>
            </div>
            <button data-testid="reply-cancel" onClick={() => setReplyTo(null)} className="text-spotter-maroon hover:text-red-700 p-1"><X className="w-4 h-4" /></button>
          </div>
        )}
        {pendingImage && (
          <div className="p-2 flex items-center gap-2" data-testid="message-image-preview">
            <div className="relative">
              <img src={mediaUrl(pendingImage)} alt="" className="h-16 w-16 object-cover rounded-lg border-2 border-black" />
              <button data-testid="message-image-remove" onClick={() => setPendingImage("")} className="absolute -top-2 -right-2 bg-black text-white rounded-full p-1"><X className="w-3 h-3" /></button>
            </div>
            <span className="text-xs text-spotter-maroon">Photo ready to send</span>
          </div>
        )}
        <div className="p-3 flex gap-2 items-center">
          <input ref={imgRef} type="file" accept="image/*" hidden onChange={pickImage} />
          <Button data-testid="message-image-btn" onClick={() => imgRef.current?.click()} disabled={imgUploading} variant="outline" className="border-2 border-black px-3 shrink-0">
            {imgUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4 text-spotter-gold" />}
          </Button>
          <GifPicker onSelect={(url) => setPendingImage(url)}
            trigger={<Button data-testid="message-gif-btn" variant="outline" className="border-2 border-black px-3 shrink-0 font-heading font-bold text-spotter-maroon">GIF</Button>} />
          <Input data-testid="message-input" value={text} onChange={onType} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…" className="border-2 border-black" />
          <Button data-testid="message-send" onClick={send} className="bg-spotter-maroon text-spotter-cream border-2 border-black"><Send className="w-4 h-4" /></Button>
        </div>
      </div>
    );
  }
}
